import { Autocomplete } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Form, Formik, FormikErrors } from "formik";
import * as React from "react";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import ErrorMsg from "../../components/ErrorMsg";
import ProgressIndicator from "../../components/ProgressIndicator";
import ShowLocationsDialog from "../../components/maps/ShowLocationsDialog";
import strings from "../../config/localization";
import {
  useGetAllLocationsQuery,
  useGetAllTemplatesQuery,
  useGetResourceTemplatesQuery,
  useNewLocationMutation,
} from "../../services/appln";
import { getDefaultLocation, getDefaultZoom } from "../constants";
import {
  getLocationStrMap,
  getParentLocationType,
  getReverseLocationStrMap,
} from "../util";

interface FormData {
  name: string;
  kind: string;
  address: string;
  parentId: string;
  personnelTemplateId: string;
  resourceTemplateId: string;
}

interface Props {
  onSubmitHandler: (newObj: any) => void;
  onCancelHandler: () => void;
  state: boolean;
  bandobastId: string;
  timeslotId: string;
  coordinate: any;
}

export default function NewLocation(props: Props) {

  const [createLocation, createLocationParams] = useNewLocationMutation();

  const [selectLocationDialogState, setSelectLocationDialogState] =
    React.useState(false);

  const [selectedLocation, setSelectedLocation] = React.useState(
    props.coordinate,
  );

  const [locationByKindMap, setLocationsByKindMap] = React.useState<
    Map<string, any>
  >(new Map());

  const [personnelTemplateMap, setPersonnelTemplateMap] = React.useState<
    Map<string, string>
  >(new Map());

  const [resourceTemplateMap, setResourceTemplateMap] = React.useState<
    Map<string, string>
  >(new Map());

  React.useEffect(() => {
    setSelectedLocation(props.coordinate);
  }, [props]);


  const iv: FormData = {
    name: "",
    kind: "",
    address: "",
    parentId: "",
    personnelTemplateId: "",
    resourceTemplateId: "",
  };

  const locationData = useGetAllLocationsQuery({
    bandobastId: props.bandobastId,
    timeslotId: props.timeslotId,
  });

  React.useEffect(() => {
    const locationMap = getLocationStrMap();
    const locationByKindMap = new Map<string, any[]>();

    if (locationData.isSuccess) {
      const data = locationData.data.data;
      console.log("location Data = ", data);
      data.forEach((location: any) => {
        const kind = locationMap.get(location.kind)!;
        if (!locationByKindMap.has(kind)) {
          locationByKindMap.set(kind, []);
        }
        locationByKindMap.get(kind)?.push(location);
      });

      setLocationsByKindMap(locationByKindMap);
      console.log("NewLocation, locationByKindMap:", locationByKindMap);
    }
  }, [locationData]);

  // TODO: there could be duplicate location names
  const locationMap = new Map<string, string>();
  locationData.data?.data.forEach((element: any) => {
    locationMap.set(element.name, element.id);
  });

  const personnelTemplateDataResponse = useGetAllTemplatesQuery({
    bandobastId: props.bandobastId,
    page: 1,
    limt: 100,
    search: "",
  });

  const resourceTemplateDataResponse = useGetResourceTemplatesQuery({
    bandobastId: props.bandobastId,
    page: 1,
    limt: 100,
    search: "",
  });

  React.useEffect(() => {
    if (personnelTemplateDataResponse.isSuccess) {
      const templateMap = new Map<string, string>();
      const data = personnelTemplateDataResponse.data.data;
      data.forEach((template: any) => {
        templateMap.set(template.name, template.id);
      });
      setPersonnelTemplateMap(templateMap);
      // console.log("NewLocation, templateMap:", templateMap);
    }
    if (resourceTemplateDataResponse.isSuccess) {
      const templateMap = new Map<string, string>();
      const data = resourceTemplateDataResponse.data.data;
      data.forEach((template: any) => {
        templateMap.set(template.name, template.id);
      });
      setResourceTemplateMap(templateMap);
      // console.log("NewLocation, templateMap:", templateMap);
    }
  }, [personnelTemplateDataResponse, resourceTemplateDataResponse]);

  const onCancelHandlerWrapper = () => {
    createLocationParams.reset();
    props.onCancelHandler();
  };

  React.useEffect(() => {
    if (createLocationParams.isSuccess) {
      console.log("NewLocation, createLocationParams:", createLocationParams);
      props.onSubmitHandler(createLocationParams.data.id);
    }
  }, [createLocationParams]);

  const getParents = (kind: string) => {
    if (!kind || kind === strings.range) {
      return [];
    }
    const parentType = getParentLocationType(kind);
    if (!locationByKindMap.has(parentType)) {
      return [];
    }
    return locationByKindMap
      .get(parentType)
      .map((e: any) => e.name)
      .toSorted();
  };

  return (
    <div>
      <Dialog
        open={props.state}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">{strings.newLocation}</DialogTitle>
        {createLocationParams.isError && (
          <ErrorMsg msg={strings.failedToCreateLocation} />
        )}
        {createLocationParams.isLoading && <ProgressIndicator />}
        <DialogContent>
          <ShowLocationsDialog
            state={selectLocationDialogState}
            data={[]}
            onClose={() => { }}
            onSelect={(location: any) => {
              console.log('XX: Selected ', location);
              setSelectedLocation(location);
              setSelectLocationDialogState(false);
            }}
            defaultZoom={getDefaultZoom()}
            defaultCenter={getDefaultLocation()}
          />
          <Formik
            initialValues={iv}
            validate={(values) => {
              const errors: FormikErrors<FormData> = {};
              if (!values.name) {
                errors.name = strings.nameMandatory;
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              console.log("XX: onSubmit, selectedLocation", selectedLocation);
              console.log('values = ', values)
              setSubmitting(false);
              const valuesClone: any = { ...values };
              valuesClone.parentId = locationMap.get(values.parentId!)!;
              valuesClone.personnelTemplateId = personnelTemplateMap.get(
                values.personnelTemplateId!,
              )!;
              valuesClone.resourceTemplateId = resourceTemplateMap.get(
                values.resourceTemplateId!,
              )!;
              valuesClone.kind = getReverseLocationStrMap().get(values.kind!)!;
              if (selectedLocation) {
                valuesClone["geoJSON"] = JSON.stringify(selectedLocation)
              }
              console.log(
                "NewLocation, values after modification",
                valuesClone,
              );
              createLocation({
                bandobastId: props.bandobastId,
                timeslotId: props.timeslotId,
                body: valuesClone,
              });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form>
                <Box sx={{ flexGrow: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <TextField
                        fullWidth
                        required
                        margin="normal"
                        id="name"
                        name="name"
                        label={strings.name}
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        id="kind"
                        options={Array.from(getReverseLocationStrMap().keys())}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            label={strings.kind}
                          />
                        )}
                        onChange={(e, value: any) => {
                          setFieldValue("kind", value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        margin="normal"
                        id="address"
                        name="address"
                        label={strings.address}
                        value={values.address}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.address && Boolean(errors.address)}
                        helperText={touched.address && errors.address}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        id="parentId"
                        options={getParents(values.kind)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            label={strings.parent}
                          />
                        )}
                        onChange={(e, value: any) => {
                          setFieldValue("parentId", value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        id="personnelTemplateId"
                        options={Array.from(personnelTemplateMap.keys()).sort()}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            label={strings.personnelTemplate}
                          />
                        )}
                        onChange={(e, value: any) => {
                          setFieldValue("personnelTemplateId", value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Autocomplete
                        fullWidth
                        disablePortal
                        id="resourceTemplateId"
                        options={Array.from(resourceTemplateMap.keys()).sort()}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            margin="normal"
                            label={strings.resourceTemplate}
                          />
                        )}
                        onChange={(e, value: any) => {
                          setFieldValue("resourceTemplateId", value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelectLocationDialogState(true);
                        }}
                      >
                        {selectedLocation
                          ? strings.selectedLocationSet
                          : strings.selectLocation}
                      </Button>
                    </Grid>
                    <Grid item xs={3}>
                      <ButtonGroup>
                        <Button
                          variant="contained"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          {strings.create}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={onCancelHandlerWrapper}
                        >
                          {strings.cancel}
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                </Box>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
}
